import { ApolloError } from '@apollo/client';
import { EventPageType, useGetFeatureSetQuery } from '@graphql/generated';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useCallback, useState, useMemo } from 'react';
import { FeatureSet, FeatureSetType } from './featureSet.types';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { isEventPageVisible } from '@shared/utils/pageVisibility';

/**
 * useFeatureSet is a hook that is responsible for retrieving the values of featureSet (currently retrieving visible pages).
 * It exports 3 properties:
 * isInitialized: This property indicates whether the featureSet was loaded correctly.
 * features: Object of type FeatureSet with preloaded featureSets.
 * hasFeatureSetEnable: Method that allows obtaining information on whether a featureSet is enabled or not for that event.
 * hasError: possible ApolloError
 */

export const useFeatureSet = () => {
  const eventNameMatch = useEventNameMatch();
  const [hasErrors, setHasErrors] = useState<Maybe<ApolloError>>(null);
  const { findWelcomePage, loading: isWelcomePageCheckingLoading, isWelcomePageAsCustomPageEnabled } = useWelcomePage();

  const { loading, refetch, data } = useGetFeatureSetQuery({
    batchMode: 'off',
    ssr: true,
    variables: {
      eventName: eventNameMatch.eventName || '',
    },
    onError(err) {
      setHasErrors(err);
    },
  });

  // Wait for all data sources before computing feature set:
  // 1. Feature set query (native page disabled flags)
  // 2. Feature flags (custom page migration state)
  const isLoading = loading || isWelcomePageCheckingLoading;

  const featureSetOpts: FeatureSet | undefined = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    // Bail out if data is missing (e.g., query failed or returned no data)
    // This prevents undefined values from being treated as "feature enabled"
    if (!data?.eventByName) {
      return undefined;
    }

    const eventByName = data.eventByName;
    const nativeWelcomePageEnabled = !eventByName.welcomePage?.disabled;
    const registryPageEnabled = !eventByName.registryPage?.disabled;
    const rsvpPageEnabled = !eventByName.rsvpPage?.disabled;

    // Check for custom welcome page when pages data and feature flags are ready
    let welcomePageEnabled = nativeWelcomePageEnabled;
    // Only check for custom welcome page if feature flag is enabled and everything is loaded
    // Type assertion needed until GraphQL types are regenerated after adding pages to query
    const pages = eventByName?.pages;
    if (isWelcomePageAsCustomPageEnabled && pages) {
      const customWelcomePage = pages.find(page => page.type === EventPageType.custom && findWelcomePage(page.pageSlug, page.type).isWelcomePage);
      // If custom welcome page exists and is visible, website featureSet should be enabled
      if (customWelcomePage && isEventPageVisible(customWelcomePage.visibility)) {
        welcomePageEnabled = true;
      }
    }

    return {
      [FeatureSetType.Website]: welcomePageEnabled,
      [FeatureSetType.Registry]: registryPageEnabled,
      [FeatureSetType.Rsvp]: rsvpPageEnabled,
    };
  }, [data, isLoading, findWelcomePage, isWelcomePageAsCustomPageEnabled]);

  const hasFeatureSetEnable = useCallback(
    (feature: FeatureSetType) => {
      return featureSetOpts ? !!featureSetOpts[feature] : false;
    },
    [featureSetOpts],
  );

  return {
    isInitialized: !isLoading && !!data?.eventByName,
    features: featureSetOpts,
    hasFeatureSetEnable,
    hasErrors,
    refetchFeatureSet: refetch,
  };
};
